import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import DOMPurify from "isomorphic-dompurify";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { categoryLabel, AUTHOR } from "@/lib/blog";
import { ArrowLeft, ArrowRight, Calendar, CalendarCheck, List, Sparkles } from "lucide-react";
import { slugify } from "@/lib/blog";
import { articleSources } from "@/data/articleSources";
import { useSeo, absoluteUrl } from "@/lib/seo";
import { complaintsForPost } from "@/data/complaints";
import authorImage from "@/assets/profile-isabella.webp";
import MikrobiomVideo from "@/components/MikrobiomVideo";
import { useLang, useT, useLocalePath, useRegisterAltPaths } from "@/i18n";
import { localizePost } from "@/lib/blog";

interface Post {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  cover_image_url: string | null;
  cover_image_alt?: string | null;
  category: string;
  published_at: string | null;
  updated_at?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  meta_keywords?: string | null;
  show_toc?: boolean | null;
  show_cta?: boolean | null;
  show_related?: boolean | null;
  title_en?: string | null;
  slug_en?: string | null;
  excerpt_en?: string | null;
  content_en?: string | null;
  meta_title_en?: string | null;
  meta_description_en?: string | null;
  meta_keywords_en?: string | null;
  cover_image_alt_en?: string | null;
}

interface TocItem {
  id: string;
  text: string;
  level: 2 | 3;
}

const processContent = (html: string): { html: string; toc: TocItem[] } => {
  const cleaned = DOMPurify.sanitize(html, {
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
  });
  if (typeof window === "undefined") return { html: cleaned, toc: [] };
  const container = document.createElement("div");
  container.innerHTML = cleaned;
  const toc: TocItem[] = [];
  const used = new Set<string>();
  container.querySelectorAll("h2, h3").forEach((node) => {
    const text = (node.textContent || "").trim();
    if (!text) return;
    let base = slugify(text) || "abschnitt";
    let id = base;
    let i = 2;
    while (used.has(id)) id = `${base}-${i++}`;
    used.add(id);
    node.setAttribute("id", id);
    toc.push({ id, text, level: node.tagName === "H2" ? 2 : 3 });
  });
  return { html: container.innerHTML, toc };
};

const SafeContent = ({ html }: { html: string }) => {
  return (
    <div
      className="prose prose-base sm:prose-lg max-w-none break-words hyphens-auto prose-headings:text-foreground prose-headings:break-words prose-p:text-foreground/85 prose-a:text-primary prose-a:break-words prose-strong:text-foreground prose-img:rounded-xl scroll-mt-24 [&_h2]:scroll-mt-24 [&_h3]:scroll-mt-24
      [&_.faq-accordion_details]:border [&_.faq-accordion_details]:border-border [&_.faq-accordion_details]:rounded-lg [&_.faq-accordion_details]:mb-3 [&_.faq-accordion_details]:bg-muted/20 [&_.faq-accordion_details]:overflow-hidden
      [&_.faq-accordion_details[open]]:bg-muted/30
      [&_.faq-accordion_summary]:cursor-pointer [&_.faq-accordion_summary]:p-4 [&_.faq-accordion_summary]:flex [&_.faq-accordion_summary]:items-center [&_.faq-accordion_summary]:justify-between [&_.faq-accordion_summary]:font-semibold [&_.faq-accordion_summary]:text-foreground [&_.faq-accordion_summary]:list-none [&_.faq-accordion_summary::-webkit-details-marker]:hidden
      [&_.faq-accordion_summary::after]:content-['+'] [&_.faq-accordion_summary::after]:text-primary [&_.faq-accordion_summary::after]:text-xl [&_.faq-accordion_summary::after]:font-light [&_.faq-accordion_summary::after]:ml-2
      [&_.faq-accordion_details[open]_summary::after]:content-['–']
      [&_.faq-accordion_details_p]:px-4 [&_.faq-accordion_details_p]:pb-4 [&_.faq-accordion_details_p]:pt-0 [&_.faq-accordion_details_p]:text-foreground/80 [&_.faq-accordion_details_p]:m-0"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const TableOfContents = ({
  items,
  tocLabel,
  basePath,
}: {
  items: TocItem[];
  tocLabel: string;
  basePath: string;
}) => {
  if (items.length < 2) return null;
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      history.replaceState(null, "", `${basePath}/${id}`);
    }
  };
  return (
    <nav
      aria-label={tocLabel}
      className="not-prose mb-10 rounded-xl border border-border bg-muted/30 p-5 md:p-6"
    >
      <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground mb-3">
        <List className="w-5 h-5 text-primary" />
        {tocLabel}
      </h2>
      <ol className="space-y-1.5 text-sm">
        {items.map((item, idx) => (
          <li key={item.id} className={item.level === 3 ? "ml-5" : ""}>
            <a
              href={`${basePath}/${item.id}`}
              onClick={(e) => handleClick(e, item.id)}
              className="text-foreground/80 hover:text-primary hover:underline transition-colors inline-flex gap-2"
            >
              {item.level === 2 && (
                <span className="text-primary font-medium tabular-nums">
                  {items.filter((i, j) => i.level === 2 && j <= idx).length}.
                </span>
              )}
              <span className="break-words hyphens-auto">{item.text}</span>
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

interface RelatedPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image_url: string | null;
  category: string;
  published_at: string | null;
  title_en?: string | null;
  slug_en?: string | null;
  excerpt_en?: string | null;
}

const BlogPost = () => {
  const { slug, section } = useParams<{ slug: string; section?: string }>();
  const lang = useLang();
  const lp = useLocalePath();
  const location = useLocation();
  const t = useT(
    {
      toc: "Inhaltsverzeichnis",
      loading: "Lade…",
      notFound: "Beitrag nicht gefunden",
      backToMagazine: "Zurück zum Magazin",
      toMagazine: "Zum Magazin",
      fallbackTitle: "Magazin – PRAXIS IB",
      writtenBy: "Verfasst von",
      authorRole: `${AUTHOR.jobTitle} · PRAXIS IB Wien`,
      authorAlt: "Isabella Bartak – akad. dipl. zert. komplementärmedizinische Therapeutin",
      disclaimer:
        "Die Inhalte dieses Artikels dienen der Aufklärung und ersetzen keine ärztliche Diagnose oder Behandlung. Bei konkreten Gesundheitsfragen vereinbaren Sie gerne einen Termin.",
      ctaTitle: "Bereit für Ihren nächsten Schritt?",
      ctaText: "Persönlich in der Praxis (Zoom nach Vereinbarung) – ich begleite Sie ganzheitlich.",
      ctaBook: "Termin vereinbaren",
      ctaServices: "Leistungen ansehen",
      readMore: "Weiterlesen",
      allPosts: "Alle Beiträge ansehen",
      locale: "de-DE",
      inLanguage: "de-AT",
      home: "Startseite",
      magazine: "Magazin",
    },
    {
      toc: "Table of contents",
      loading: "Loading…",
      notFound: "Article not found",
      backToMagazine: "Back to the magazine",
      toMagazine: "To the magazine",
      fallbackTitle: "Magazine – PRAXIS IB",
      writtenBy: "Written by",
      authorRole: `${AUTHOR.jobTitleEn} · PRAXIS IB Vienna`,
      authorAlt: "Isabella Bartak – certified complementary medicine therapist",
      disclaimer:
        "The content of this article is for information purposes and does not replace medical diagnosis or treatment. For specific health concerns, you are welcome to book an appointment.",
      ctaTitle: "Ready for your next step?",
      ctaText: "In person at the practice (Zoom by arrangement) – I accompany you holistically.",
      ctaBook: "Book an appointment",
      ctaServices: "View services",
      readMore: "Continue reading",
      allPosts: "View all articles",
      locale: "en-GB",
      inLanguage: "en",
      home: "Home",
      magazine: "Magazine",
    },
  );
  const [post, setPost] = useState<Post | null>(null);
  const [related, setRelated] = useState<RelatedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("blog_posts")
        .select("*")
        .or(`slug.eq.${slug},slug_en.eq.${slug}`)
        .eq("published", true)
        .maybeSingle();
      if (!data) {
        setNotFound(true);
      } else {
        setPost(data);
        if ((data as any).show_related === false) {
          setRelated([]);
        } else {
        // Fetch related: same category first, then fill with recent
        const sameCat = await supabase
          .from("blog_posts")
          .select("id,title,slug,excerpt,cover_image_url,category,published_at,title_en,slug_en,excerpt_en")
          .eq("published", true)
          .eq("category", data.category)
          .neq("id", data.id)
          .order("published_at", { ascending: false })
          .limit(3);
        let items: RelatedPost[] = sameCat.data ?? [];
        if (items.length < 3) {
          const exclude = [data.id, ...items.map((p) => p.id)];
          const recent = await supabase
            .from("blog_posts")
            .select("id,title,slug,excerpt,cover_image_url,category,published_at,title_en,slug_en,excerpt_en")
            .eq("published", true)
            .not("id", "in", `(${exclude.join(",")})`)
            .order("published_at", { ascending: false })
            .limit(3 - items.length);
          items = [...items, ...(recent.data ?? [])];
        }
        setRelated(items);
        }
      }
      setLoading(false);
    })();
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) return;

      const key = `ibpraxis:view:${post.id}`;
      if (window.sessionStorage.getItem(key)) return;

      try {
        await supabase.rpc("track_blog_view", {
          _post_id: post.id,
          _lang: lang,
        });
        window.sessionStorage.setItem(key, "1");
      } catch {
        // ignore tracking errors
      }
    })();
  }, [post, lang]);

  // Base path of the article (without an optional /:section suffix) – used for TOC links.
  const basePath = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    if (section && parts.length > 1 && parts[parts.length - 1] === section) {
      return "/" + parts.slice(0, -1).join("/");
    }
    return location.pathname;
  }, [location.pathname, section]);

  // When the article is opened via /blog/<slug>/<section> (or an old #hash link),
  // jump to that section.
  useEffect(() => {
    if (!post) return;
    const target = section || window.location.hash.replace(/^#/, "");
    if (!target) return;
    let cancelled = false;
    let timer: number | undefined;
    const tryScroll = (attempt: number) => {
      if (cancelled) return;
      const el = document.getElementById(target);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempt < 25) timer = window.setTimeout(() => tryScroll(attempt + 1), 150);
    };
    tryScroll(0);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [post, section, location.hash]);

  const L = post ? localizePost(post, lang) : null;
  useRegisterAltPaths(
    post ? { de: `/blog/${post.slug}`, en: post.slug_en ? `/blog/${post.slug_en}` : null } : null,
  );

  const seoTitle = post
    ? (L!.metaTitle?.trim() ? L!.metaTitle : `${L!.title} | ${t.magazine} – PRAXIS IB`)
    : t.fallbackTitle;
  const seoDescription = L?.metaDescription?.trim() || L?.excerpt || undefined;
  const seoKeywords = L?.metaKeywords?.trim() || undefined;
  const canonical = L ? `/blog/${L.slug}` : "/blog";
  const ogImage = post?.cover_image_url
    ? (post.cover_image_url.startsWith("http") ? post.cover_image_url : absoluteUrl(post.cover_image_url))
    : undefined;

  const plainText = useMemo(
    () => (L?.content ?? "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(),
    [L?.content]
  );
  const wordCount = useMemo(
    () => (plainText ? plainText.split(/\s+/).filter(Boolean).length : 0),
    [plainText]
  );
  const readingMinutes = Math.max(1, Math.round(wordCount / 220));

  const tagList = useMemo(
    () =>
      (L?.metaKeywords || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    [L?.metaKeywords]
  );

  const jsonLd = useMemo(() => {
    if (!post) return null;
    return [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: L!.title,
        description: L!.excerpt ?? undefined,
        image: post.cover_image_url ? [post.cover_image_url] : undefined,
        datePublished: post.published_at,
        dateModified: post.updated_at ?? post.published_at,
        articleSection: categoryLabel(post.category, lang),
        keywords: tagList.length ? tagList.join(", ") : undefined,
        wordCount: wordCount || undefined,
        timeRequired: wordCount ? `PT${readingMinutes}M` : undefined,
        inLanguage: t.inLanguage,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": absoluteUrl(canonical),
        },
        author: {
          "@type": "Person",
          name: AUTHOR.name,
          jobTitle: lang === "en" ? AUTHOR.jobTitleEn : AUTHOR.jobTitle,
          url: AUTHOR.url,
          worksFor: { "@type": "Organization", name: "PRAXIS IB", url: "https://ibpraxis.net" },
        },
        publisher: {
          "@type": "Organization",
          name: "PRAXIS IB",
          logo: {
            "@type": "ImageObject",
            url: "https://ibpraxis.net/og.png",
          },
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t.home, item: absoluteUrl("/") },
          { "@type": "ListItem", position: 2, name: t.magazine, item: absoluteUrl("/blog") },
          { "@type": "ListItem", position: 3, name: L!.title, item: absoluteUrl(canonical) },
        ],
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, wordCount, readingMinutes, tagList, lang]);

  useSeo({
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
    canonical,
    ogImage,
    ogImageAlt: L?.coverImageAlt || L?.title,
    ogImageWidth: ogImage ? 1200 : undefined,
    ogImageHeight: ogImage ? 630 : undefined,
    ogType: "article",
    article: post
      ? {
          publishedTime: post.published_at,
          modifiedTime: post.updated_at ?? post.published_at,
          author: AUTHOR.name,
          section: categoryLabel(post.category, lang),
          tags: tagList,
        }
      : undefined,
    jsonLd,
    jsonLdId: "blog-post-jsonld",
  });


  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-32 text-center text-muted-foreground">{t.loading}</div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-32 pb-20 text-center container mx-auto px-4">
          <h1 className="text-3xl font-bold mb-4">{t.notFound}</h1>
          <Button asChild><Link to={lp("/blog")}>{t.backToMagazine}</Link></Button>
        </div>
        <Footer />
      </div>
    );
  }

  const date = post.published_at
    ? new Date(post.published_at).toLocaleDateString(t.locale, { day: "2-digit", month: "long", year: "numeric" })
    : "";

  const { html: processedHtml, toc } = processContent(L!.content);

  // Add ItemList JSON-LD for TOC → enables Google sitelinks to anchors
  const tocJsonLd = toc.length >= 2
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `${t.toc}: ${L!.title}`,
        itemListElement: toc.map((item, idx) => ({
          "@type": "ListItem",
          position: idx + 1,
          name: item.text,
          url: `${absoluteUrl(canonical)}/${item.id}`,
        })),
      }
    : null;

  if (tocJsonLd && Array.isArray(jsonLd) && !jsonLd.some((j: any) => j["@type"] === "ItemList")) {
    (jsonLd as any[]).push(tocJsonLd);
  }

  const formatRelDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(t.locale, { day: "2-digit", month: "short", year: "numeric" }) : "";

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <article className="pt-24 pb-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <Button variant="ghost" size="sm" asChild className="mb-6">
            <Link to={lp("/blog")}><ArrowLeft className="w-4 h-4 mr-2" />{t.toMagazine}</Link>
          </Button>

          <div className="mb-6">
            <Badge variant="secondary" className="mb-4">{categoryLabel(post.category, lang)}</Badge>
            <h1 className="text-[1.6rem] sm:text-3xl md:text-5xl font-bold text-foreground mb-4 leading-tight break-words hyphens-auto [overflow-wrap:anywhere]" lang={lang === "en" ? "en" : "de"}>
              {L!.title}
            </h1>
            {L!.excerpt && (
              <p className="text-base sm:text-lg text-muted-foreground mb-4 break-words hyphens-auto">{L!.excerpt}</p>
            )}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />{date}
            </div>
          </div>

          {post.cover_image_url && (
            <div className="rounded-2xl overflow-hidden mb-10 aspect-[16/9] bg-muted">
              <img
                src={post.cover_image_url}
                alt={L!.coverImageAlt || L!.title}
                width={1600}
                height={900}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {post.show_toc !== false && (
            <TableOfContents items={toc} tocLabel={t.toc} basePath={basePath} />
          )}
          <SafeContent html={processedHtml} />

          {post.slug === "leaky-gut-schleimhautpermeabilitaet-iga-lymphozyten" && (
            <div className="not-prose mt-12 -mx-4 sm:mx-0">
              <MikrobiomVideo />
            </div>
          )}

          {articleSources[post.slug] && (
            <section className="not-prose mt-12 rounded-xl border border-border bg-muted/20 p-5 md:p-6">
              <h2 className="text-lg font-semibold text-foreground mb-2">
                {lang === "en" ? "Sources and further reading" : "Quellen und weiterführende Literatur"}
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                {lang === "en"
                  ? "The following publications, guidelines and standard works form the basis of this article."
                  : "Die folgenden Fachpublikationen, Leitlinien und Standardwerke bilden die inhaltliche Grundlage dieses Beitrags."}
              </p>
              <ol className="space-y-2 list-decimal pl-5">
                {articleSources[post.slug].map((ref) => (
                  <li key={ref} className="text-sm text-foreground/80 leading-relaxed">
                    {ref}
                  </li>
                ))}
              </ol>
            </section>
          )}



          {/* Author Box */}
          <aside className="not-prose mt-10 rounded-xl border border-border bg-muted/20 p-5 md:p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <img
                src={authorImage}
                alt={t.authorAlt}
                width={96}
                height={96}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-primary/20 shrink-0"
                loading="lazy"
              />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground mb-1">{t.writtenBy}</p>
                <h2 className="text-lg font-semibold text-foreground mb-1">
                  <Link to={lp("/") + "#ueber-mich"} className="hover:text-primary transition-colors">
                    {AUTHOR.name}
                  </Link>
                </h2>
                <p className="text-sm text-muted-foreground mb-3">
                  {t.authorRole}
                </p>
                <p className="text-sm text-foreground/80 leading-relaxed">{t.disclaimer}</p>
              </div>
            </div>
          </aside>


          {/* CTA-Box (dezent) */}
          {post.show_cta !== false && (
          <aside className="mt-12 rounded-xl border border-border bg-muted/20 p-5 md:p-6">
            <div className="flex items-start gap-3 mb-4">
              <Sparkles className="w-4 h-4 text-primary/70 mt-1 shrink-0" />
              <div>
                <h2 className="text-base md:text-lg font-semibold text-foreground mb-1">
                  {t.ctaTitle}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t.ctaText}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:ml-7">
              <Button asChild size="sm" variant="outline">
                <Link to={lp("/") + "#kontakt"}>
                  <CalendarCheck className="w-4 h-4 mr-2" />
                  {t.ctaBook}
                </Link>
              </Button>
              <Button asChild size="sm" variant="ghost">
                <Link to={lp("/") + "#leistungen"}>
                  {t.ctaServices}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </aside>
          )}

          {/* Weiterlesen */}
          {post.show_related !== false && related.length > 0 && (
            <section className="mt-14 pt-10 border-t border-border" aria-labelledby="weiterlesen-heading">
              <h2 id="weiterlesen-heading" className="text-2xl font-bold text-foreground mb-6">
                {t.readMore}
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {related.map((p) => (
                  <Link
                    key={p.id}
                    to={lp(`/blog/${(lang === "en" && p.slug_en) || p.slug}`)}
                    className="group rounded-xl overflow-hidden border border-border bg-card hover:shadow-lg hover:border-primary/30 transition-all flex flex-col"
                  >
                    {p.cover_image_url && (
                      <div className="aspect-[16/10] overflow-hidden bg-muted">
                        <img
                          src={p.cover_image_url}
                          alt={(lang === "en" && p.title_en) || p.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-4 flex-1 flex flex-col">
                      <Badge variant="secondary" className="w-fit mb-2 text-xs">
                        {categoryLabel(p.category, lang)}
                      </Badge>
                      <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {(lang === "en" && p.title_en) || p.title}
                      </h3>
                      {((lang === "en" && p.excerpt_en) || p.excerpt) && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3 flex-1">{(lang === "en" && p.excerpt_en) || p.excerpt}</p>
                      )}
                      <div className="text-xs text-muted-foreground mt-auto">{formatRelDate(p.published_at)}</div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-6 text-center">
                <Button asChild variant="ghost">
                  <Link to={lp("/blog")}>
                    {t.allPosts}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </section>
          )}
        </div>
      </article>
      <Footer />
    </div>
  );
};

export default BlogPost;
